import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import StarekContext from "../context/StarekContext";
import { fetchDatapoints, detectTransitions } from "../utils/productionCalculations";

// ── Constants ─────────────────────────────────────────────────────────

const DAY_SECS      = 86400;
const BUCKETS       = 60;
const MAX_UPCOMING  = 3;
const LOOKAHEAD     = 14;
const PALETTE       = ["#0073b7", "#28a745", "#e83e8c", "#6f42c1", "#17a2b8", "#fd7e14"];

const STATE_CONFIG = {
    active:  { color: "#28a745", label: "Active"  },
    stopped: { color: "#dc3545", label: "Stopped" },
    idle:    { color: "#e67e22", label: "Idle"    },
    nodata:  { color: "#e9ecef", label: "No data", border: true },
};

// ── Shared helpers ────────────────────────────────────────────────────

const secToLabel = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60).toString().padStart(2, "0");
    return `${h % 12 || 12}:${m}${h >= 12 ? "pm" : "am"}`;
};

const runsDow = (shift, dateStr) => {
    if (!shift.runs_on) return true;
    const dow = moment(dateStr + "T12:00:00").day();
    return (shift.runs_on & (1 << dow)) !== 0;
};

function findLastCompletedShift(shifts) {
    const now = moment();
    const instances = [];
    for (let daysAgo = 0; daysAgo <= 7; daysAgo++) {
        const dateStr = moment().subtract(daysAgo, "days").format("YYYY-MM-DD");
        for (const shift of shifts) {
            if (!runsDow(shift, dateStr)) continue;
            const start = moment(dateStr).startOf("day").add(shift.start_time, "seconds");
            const end   = moment(dateStr).startOf("day").add(shift.end_time,   "seconds");
            if (shift.end_time < shift.start_time) end.add(1, "day");
            if (end.isBefore(now)) instances.push({ shift, start, end });
        }
        if (instances.length > 0 && daysAgo > 0) break;
    }
    instances.sort((a, b) => b.end.valueOf() - a.end.valueOf());
    return instances[0] || null;
}

function findUpcomingInstances(shifts) {
    const now = moment();
    const results = [];
    for (let d = 0; d <= LOOKAHEAD && results.length < MAX_UPCOMING; d++) {
        const date    = moment().add(d, "days");
        const dateStr = date.format("YYYY-MM-DD");
        const dow     = date.day();
        const sorted  = [...(shifts || [])].filter(s => !s.runs_on || (s.runs_on & (1 << dow))).sort((a, b) => a.start_time - b.start_time);
        for (const shift of sorted) {
            if (results.length >= MAX_UPCOMING) break;
            const start = moment(dateStr).startOf("day").add(shift.start_time, "seconds");
            const end   = moment(dateStr).startOf("day").add(shift.end_time,   "seconds");
            if (shift.end_time < shift.start_time) end.add(1, "day");
            if (end.isSameOrBefore(now) || start.isSameOrBefore(now)) continue;
            results.push({ shift, start, end, dateStr });
        }
    }
    return results;
}

function computeBuckets(device, shiftStart, shiftEnd, deviceSignalData, types) {
    const shiftDurMs = shiftEnd.valueOf() - shiftStart.valueOf();
    const bucketMs   = shiftDurMs / BUCKETS;
    const hasData    = Object.values(deviceSignalData).some(r => r.length > 0);
    const stats      = Array.from({ length: BUCKETS }, () => ({ readings: 0, transitions: 0 }));

    const signals = device.device?.runtime?.signals || [];
    (Array.isArray(signals) ? signals : []).forEach(signal => {
        const readings = deviceSignalData[signal.name] || [];
        if (!readings.length) return;
        readings.forEach(r => {
            const idx = Math.floor((r.time * 1000 - shiftStart.valueOf()) / bucketMs);
            if (idx >= 0 && idx < BUCKETS) stats[idx].readings++;
        });
        detectTransitions(readings, signal, null, types).forEach(t => {
            const idx = Math.floor((t.timestamp * 1000 - shiftStart.valueOf()) / bucketMs);
            if (idx >= 0 && idx < BUCKETS) stats[idx].transitions++;
        });
    });

    return stats.map((b, i) => {
        if (!hasData || b.readings === 0) return "nodata";
        const bStart = shiftStart.valueOf() + i * bucketMs;
        const bEnd   = bStart + bucketMs;
        const hasJob = (device.jobSchedules || []).some(js => {
            const jS = moment(js.start_time).valueOf();
            const jE = moment(js.end_time).valueOf();
            return jS < bEnd && jE > bStart;
        });
        if (b.transitions > 0) return "active";
        if (hasJob) return "stopped";
        return "idle";
    });
}

// ── Section divider ───────────────────────────────────────────────────

function SectionDivider({ title, subtitle, collapsed, onToggle }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0 8px", borderTop: "1px solid #e9ecef", marginTop: 2 }}>
            <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#495057", whiteSpace: "nowrap" }}>
                {title}
            </span>
            {subtitle && (
                <span style={{ fontSize: "0.7rem", color: "#868e96", whiteSpace: "nowrap" }}>{subtitle}</span>
            )}
            <div style={{ flex: 1, height: 1, background: "#e9ecef" }} />
            <button type="button" className="btn btn-sm p-0" style={{ lineHeight: 1, color: "#868e96" }} onClick={onToggle}>
                <i className={`fa ${collapsed ? "fa-chevron-down" : "fa-chevron-up"}`} style={{ fontSize: "0.65rem" }} />
            </button>
        </div>
    );
}

// ── Section 1: Today's Shifts ─────────────────────────────────────────

function TodaySection({ shifts }) {
    const [collapsed, setCollapsed] = useState(false);
    const now        = moment();
    const todayDow   = now.day();
    const currentSec = now.hours() * 3600 + now.minutes() * 60 + now.seconds();

    const todayShifts = useMemo(() =>
        (shifts || []).filter(s => !s.runs_on || (s.runs_on & (1 << todayDow))),
    [shifts, todayDow]);

    const status = (s) => {
        const end = s.end_time < s.start_time ? DAY_SECS : s.end_time;
        if (currentSec >= s.start_time && currentSec <= end) return "active";
        if (currentSec < s.start_time) return "upcoming";
        return "done";
    };

    return (
        <>
            <SectionDivider title="Today's Shifts" subtitle={now.format("ddd, MMM D")} collapsed={collapsed} onToggle={() => setCollapsed(v => !v)} />
            {!collapsed && (
                todayShifts.length === 0 ? (
                    <p className="text-muted small mb-1 py-1">No shifts scheduled for today.</p>
                ) : (
                    <div style={{ paddingBottom: 10 }}>
                        <div style={{ position: "relative", height: 32, background: "#f4f6f9", borderRadius: 6, border: "1px solid #dee2e6", overflow: "hidden", marginBottom: 8 }}>
                            {todayShifts.map((s, i) => {
                                const left  = `${(s.start_time / DAY_SECS) * 100}%`;
                                const end   = s.end_time < s.start_time ? DAY_SECS : s.end_time;
                                const width = `${((end - s.start_time) / DAY_SECS) * 100}%`;
                                const st    = status(s);
                                return (
                                    <div key={s.id} title={`${s.name}: ${secToLabel(s.start_time)} – ${secToLabel(s.end_time)}`}
                                        style={{
                                            position: "absolute", top: 3, height: 26, left, width,
                                            background: PALETTE[i % PALETTE.length],
                                            opacity: st === "done" ? 0.3 : st === "active" ? 1 : 0.65,
                                            borderRadius: 4, overflow: "hidden",
                                            display: "flex", alignItems: "center", paddingLeft: 6,
                                        }}>
                                        <span style={{ color: "#fff", fontSize: "0.67rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                                            {s.name}
                                        </span>
                                    </div>
                                );
                            })}
                            <div style={{ position: "absolute", top: 0, bottom: 0, left: `${(currentSec / DAY_SECS) * 100}%`, width: 2, background: "#dc3545" }}>
                                <span style={{ position: "absolute", top: 1, left: 4, fontSize: "0.55rem", color: "#dc3545", fontWeight: 700, whiteSpace: "nowrap" }}>
                                    {now.format("h:mma")}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {todayShifts.map((s, i) => {
                                const st = status(s);
                                return (
                                    <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: 2, background: PALETTE[i % PALETTE.length], opacity: st === "done" ? 0.35 : 1 }} />
                                        <span style={{ fontSize: "0.72rem", color: st === "done" ? "#adb5bd" : "#495057" }}>
                                            {s.name} · {secToLabel(s.start_time)}–{secToLabel(s.end_time)}
                                            {st === "active" && <strong style={{ color: PALETTE[i % PALETTE.length], marginLeft: 4 }}>● Now</strong>}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )
            )}
        </>
    );
}

// ── Section 2: Last Shift Overview ────────────────────────────────────

function LastShiftSection({ devices, shifts }) {
    const [collapsed, setCollapsed] = useState(false);
    const { lastUsedWorkspace, auth: authToken, types } = useContext(StarekContext);
    const [fetching, setFetching] = useState(false);
    const [rawData, setRawData]   = useState(null);

    const shiftInstance = useMemo(() => shifts?.length ? findLastCompletedShift(shifts) : null, [shifts]);

    useEffect(() => {
        if (!shiftInstance || !devices?.length || !lastUsedWorkspace || !authToken) return;
        const deviceIds = devices.map(d => d.id);
        const startTime = Math.floor(shiftInstance.start.valueOf() / 1000);
        const endTime   = Math.ceil(shiftInstance.end.valueOf()   / 1000);
        setFetching(true);
        setRawData(null);
        fetchDatapoints({ deviceIds, startTime, endTime, workspace: lastUsedWorkspace, authToken })
            .then(setRawData).catch(() => setRawData(null)).finally(() => setFetching(false));
    }, [shiftInstance, devices, lastUsedWorkspace, authToken]);

    const rows = useMemo(() => {
        if (!shiftInstance || !devices?.length) return [];
        return devices.map(device => ({
            device,
            buckets: computeBuckets(device, shiftInstance.start, shiftInstance.end, rawData?.data?.[device.id] || {}, types),
        }));
    }, [shiftInstance, devices, rawData, types]);

    const subtitle = shiftInstance
        ? `${shiftInstance.shift.name} · ${shiftInstance.start.format("ddd MMM D, h:mma")} – ${shiftInstance.end.format("h:mma")}`
        : null;

    // Fleet heatmap: per-bucket aggregate
    const heatBuckets = useMemo(() => {
        if (!rows.length) return [];
        const total = rows.length;
        return Array.from({ length: BUCKETS }, (_, i) => {
            const counts = { active: 0, stopped: 0, idle: 0, nodata: 0 };
            rows.forEach(({ buckets }) => counts[buckets[i]]++);
            return counts;
        });
    }, [rows]);

    return (
        <>
            <SectionDivider title="Last Shift" subtitle={subtitle} collapsed={collapsed} onToggle={() => setCollapsed(v => !v)} />
            {!collapsed && (
                fetching ? (
                    <div className="text-center py-3"><i className="fa fa-spinner fa-spin text-muted" /></div>
                ) : !shiftInstance ? (
                    <p className="text-muted small mb-1 py-1">No completed shifts found.</p>
                ) : (
                    <div style={{ paddingBottom: 10 }}>
                        {/* Legend */}
                        <div style={{ display: "flex", gap: 10, marginBottom: 6 }}>
                            {Object.entries(STATE_CONFIG).map(([key, { color, label, border }]) => (
                                <div key={key} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                    <span style={{ width: 9, height: 9, borderRadius: 2, background: color, display: "inline-block", border: border ? "1px solid #ced4da" : "none" }} />
                                    <span style={{ fontSize: "0.65rem", color: "#6c757d" }}>{label}</span>
                                </div>
                            ))}
                        </div>

                        {/* Fleet heatmap */}
                        {heatBuckets.length > 0 && (
                            <div style={{ display: "flex", paddingLeft: 160, marginBottom: 4, gap: 8 }}>
                                <div style={{ flex: 1, display: "flex", height: 14, borderRadius: 3, overflow: "hidden", border: "1px solid #dee2e6" }}>
                                    {heatBuckets.map((counts, i) => {
                                        const total = rows.length;
                                        const tip = `Active: ${counts.active} · Stopped: ${counts.stopped} · Idle: ${counts.idle} · No data: ${counts.nodata}`;
                                        return (
                                            <div key={i} title={tip} style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                                <div style={{ flex: counts.active,  background: STATE_CONFIG.active.color  }} />
                                                <div style={{ flex: counts.stopped, background: STATE_CONFIG.stopped.color }} />
                                                <div style={{ flex: counts.idle,    background: STATE_CONFIG.idle.color    }} />
                                                <div style={{ flex: counts.nodata,  background: STATE_CONFIG.nodata.color  }} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Time axis */}
                        <div style={{ display: "flex", paddingLeft: 160, marginBottom: 4, position: "relative", height: 16 }}>
                            {[0, 0.25, 0.5, 0.75, 1].map(pct => {
                                const t = moment(shiftInstance.start.valueOf() + pct * (shiftInstance.end.valueOf() - shiftInstance.start.valueOf()));
                                return (
                                    <span key={pct} style={{ position: "absolute", left: `${pct * 100}%`, transform: "translateX(-50%)", fontSize: "0.63rem", color: "#6c757d", whiteSpace: "nowrap" }}>
                                        {t.format("h:mma")}
                                    </span>
                                );
                            })}
                        </div>

                        {/* Per-device rows */}
                        <div style={{ overflowY: "auto", maxHeight: 280 }}>
                            {rows.map(({ device, buckets }) => (
                                <div key={device.id} style={{ display: "flex", alignItems: "center", marginBottom: 4, gap: 8 }}>
                                    <div style={{ width: 152, flexShrink: 0, fontSize: "0.76rem", fontWeight: 600, color: "#212529", textAlign: "right", paddingRight: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={device.name || device.id}>
                                        {device.name || device.id}
                                    </div>
                                    <div style={{ flex: 1, display: "flex", height: 20, borderRadius: 3, overflow: "hidden", border: "1px solid #dee2e6" }}>
                                        {buckets.map((state, i) => (
                                            <div key={i} style={{ flex: 1, background: STATE_CONFIG[state]?.color || STATE_CONFIG.nodata.color }}
                                                title={`${moment(shiftInstance.start.valueOf() + i * (shiftInstance.end.valueOf() - shiftInstance.start.valueOf()) / BUCKETS).format("h:mma")} — ${STATE_CONFIG[state]?.label}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}
        </>
    );
}

// ── Section 3: Upcoming Shifts ────────────────────────────────────────

function UpcomingSection({ devices, shifts }) {
    const [collapsed, setCollapsed] = useState(false);

    const instances = useMemo(() => findUpcomingInstances(shifts), [shifts]);

    const totalGaps = useMemo(() =>
        instances.reduce((sum, inst) =>
            sum + (devices || []).filter(d =>
                !(d.jobSchedules || []).some(js =>
                    moment(js.start_time).isBefore(inst.end) && moment(js.end_time).isAfter(inst.start)
                )
            ).length,
        0),
    [instances, devices]);

    return (
        <>
            <SectionDivider
                title="Upcoming Shifts"
                subtitle={totalGaps > 0 ? `${totalGaps} scheduling ${totalGaps === 1 ? "gap" : "gaps"}` : null}
                collapsed={collapsed}
                onToggle={() => setCollapsed(v => !v)}
            />
            {!collapsed && (
                instances.length === 0 ? (
                    <p className="text-muted small mb-1 py-1">No upcoming shifts in the next {LOOKAHEAD} days.</p>
                ) : (
                    <div style={{ paddingBottom: 4 }}>
                        {instances.map((inst, idx) => {
                            const isToday    = inst.start.isSame(moment(), "day");
                            const isTomorrow = inst.start.isSame(moment().add(1, "day"), "day");
                            const dayLabel   = isToday ? "Today" : isTomorrow ? "Tomorrow" : inst.start.format("ddd MMM D");
                            const deviceRows = (devices || []).map(device => ({
                                device,
                                jobs: (device.jobSchedules || []).filter(js =>
                                    moment(js.start_time).isBefore(inst.end) && moment(js.end_time).isAfter(inst.start)
                                ),
                            }));
                            const assigned = deviceRows.filter(r => r.jobs.length > 0).length;
                            const gaps     = deviceRows.filter(r => r.jobs.length === 0).length;
                            return (
                                <div key={idx} style={{ marginBottom: 10 }}>
                                    <div style={{ background: "#f8f9fa", padding: "6px 10px", borderLeft: "3px solid #007bff", borderRadius: "0 4px 4px 0", display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                                        <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#212529" }}>{inst.shift.name}</span>
                                        <span style={{ fontSize: "0.73rem", color: "#6c757d" }}>{dayLabel} · {inst.start.format("h:mma")}–{inst.end.format("h:mma")}</span>
                                        <div style={{ marginLeft: "auto", display: "flex", gap: 5 }}>
                                            {assigned > 0 && <span className="badge badge-success" style={{ fontSize: "0.65rem" }}>{assigned} assigned</span>}
                                            {gaps > 0 && <span className="badge badge-warning" style={{ fontSize: "0.65rem" }}>{gaps} unscheduled</span>}
                                        </div>
                                    </div>
                                    {deviceRows.map(({ device, jobs }) => (
                                        <div key={device.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "4px 10px 4px 18px", borderBottom: "1px solid #f4f4f4" }}>
                                            <div style={{ width: 160, flexShrink: 0, fontSize: "0.77rem", fontWeight: 600, color: "#212529", paddingTop: 2 }}>
                                                {device.name || device.id}
                                            </div>
                                            {jobs.length === 0 ? (
                                                <div style={{ fontSize: "0.74rem", color: "#fd7e14", paddingTop: 2 }}>
                                                    <i className="fa fa-exclamation-triangle mr-1" />No job scheduled
                                                </div>
                                            ) : (
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                                    {jobs.map(job => (
                                                        <div key={job.id} style={{ background: "#edf7ee", border: "1px solid #c3e6cb", borderRadius: 4, padding: "2px 7px", fontSize: "0.73rem", display: "flex", alignItems: "center", gap: 4 }}>
                                                            {job.part?.name && <span style={{ fontWeight: 700, color: "#155724" }}><i className="fa fa-cube mr-1" style={{ fontSize: "0.65rem" }} />{job.part.name}</span>}
                                                            {job.cycle?.name && <span style={{ color: "#28a745" }}>· {job.cycle.name}</span>}
                                                            <span style={{ color: "#6c757d", fontSize: "0.67rem" }}>{moment(job.start_time).format("h:mma")}–{moment(job.end_time).format("h:mma")}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                        <div className="starek-card-footer" style={{ paddingTop: 6 }}>
                            <Link to="/job-schedules" className="starek-card-footer-link">
                                Manage job schedules <i className="fa fa-arrow-right" />
                            </Link>
                        </div>
                    </div>
                )
            )}
        </>
    );
}

// ── Main card ─────────────────────────────────────────────────────────

function ShiftsOverview({ devices, shifts, loading }) {
    const [collapsed, setCollapsed] = useState(false);

    return (
        <div className={`card card-outline card-primary ${collapsed ? "collapsed-card" : ""}`} style={{ position: "relative" }}>
            {loading && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: "inherit" }}>
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem", color: "#6c757d" }} />
                </div>
            )}
            <div className="card-header py-2">
                <h3 className="card-title">
                    <i className="fa fa-calendar mr-2" />
                    Shifts Overview
                </h3>
                <div className="card-tools">
                    <button type="button" className="btn btn-tool" onClick={() => setCollapsed(v => !v)}>
                        <i className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`} />
                    </button>
                </div>
            </div>
            <div className={`card-body py-2 ${collapsed ? "d-none" : ""}`}>
                <TodaySection shifts={shifts} />
                <LastShiftSection devices={devices} shifts={shifts} />
                <UpcomingSection devices={devices} shifts={shifts} />
            </div>
        </div>
    );
}

export default ShiftsOverview;
